class MatchDataService {

  static async pullMatchData(matchId) {
    if (!matchId || typeof matchId !== 'string') {
      throw new Error('Valid match ID is required');
    }

    try {
      // Call Firebase Cloud Function
      // URL format: https://<region>-<project-id>.cloudfunctions.net/pullMatchData
      // For production, get this from your Firebase config
      const functionUrl = process.env.REACT_APP_PULL_MATCH_DATA_URL ||
                         'https://us-central1-draftsimulatorapp.cloudfunctions.net/pullMatchData';

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ matchId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Invalid response from server');
      }

      return result.data;

    } catch (error) {
      console.error('Error pulling match data:', error);

      // Provide user-friendly error messages
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        throw new Error('Network error. Please check your internet connection.');
      }

      throw error;
    }
  }

  // Fallback method to look for the most recent match data file
  static async pullMatchDataFromFile(matchId) {
    try {
      // Since we can't directly access the file system from browser,
      // we'll use a trick - try to fetch the file via the dev server
      // First, we need to find the most recent file
      
      // Try to fetch a few possible recent timestamps
      const now = Date.now();
      const possibleTimestamps = [];
      
      // Generate possible timestamps for the last 10 minutes
      for (let i = 0; i < 10; i++) {
        possibleTimestamps.push(now - (i * 60000)); // Every minute back
      }
      
      // Try to fetch each possible file
      for (const timestamp of possibleTimestamps) {
        try {
          const fileName = `Match_${matchId}_Draft_2025-09-07_${timestamp}.json`;
          const response = await fetch(`/data/${fileName}`);
          
          if (response.ok) {
            const data = await response.json();
            return data;
          }
        } catch (e) {
          // Continue trying other timestamps
          continue;
        }
      }
      
      // If no specific timestamp works, try a generic pattern
      // This will require the file to be copied to public/data folder
      throw new Error('MANUAL_PROCESS_REQUIRED');
      
    } catch (error) {
      throw new Error('MANUAL_PROCESS_REQUIRED');
    }
  }

  static parseMatchDataToPicksFormat(matchData) {
    if (!matchData || !matchData.scenariosFirstPick || !Array.isArray(matchData.scenariosFirstPick)) {
      throw new Error('Invalid match data format');
    }

    // Get the first scenario's picks
    const firstScenario = matchData.scenariosFirstPick[0];
    
    if (!firstScenario || !Array.isArray(firstScenario.picks)) {
      throw new Error('No picks found in match data');
    }

    // Convert the external format to our picks format
    const picks = Array(24).fill(null);
    
    firstScenario.picks.forEach((pick, index) => {
      if (pick && pick.hero && index < 24) {
        picks[index] = {
          hero: {
            id: pick.hero.id,
            name: pick.hero.name,
            imageUrl: pick.hero.imageUrl,
            attributes: pick.hero.attributes
          },
          isBanned: pick.isBanned || false,
          position: index + 1
        };
      }
    });

    const result = {
      picks,
      title: matchData.title || `Match ${matchData.id || 'Unknown'}`,
      teamName: matchData.teamName || '',
      tournament: matchData.tournament || '',
      date: matchData.date || new Date().toISOString().slice(0, 10),
      originalData: matchData
    };
    
    return result;
  }

  static async loadMatchDataFile() {
    try {
      // Create a file input element
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json';
      
      return new Promise((resolve, reject) => {
        input.onchange = async (e) => {
          const file = e.target.files[0];
          if (!file) {
            reject(new Error('No file selected'));
            return;
          }
          
          try {
            const text = await file.text();
            const data = JSON.parse(text);
            resolve(data);
          } catch (error) {
            reject(new Error(`Failed to parse file: ${error.message}`));
          }
        };
        
        input.oncancel = () => {
          reject(new Error('File selection cancelled'));
        };
        
        input.click();
      });
    } catch (error) {
      console.error('Error loading match data file:', error);
      throw error;
    }
  }
}

export default MatchDataService;